import React, { useContext, useMemo, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ExtraCostsPreviewOutDTO } from '@api/ringil-api.v2';
import { isNotNullOrUndefined } from '@utils/utils';
import RingilDataTable from '@components/ringil3/RingilDataTable/RingilDataTable';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import Badge from '@components/ringil3/Badges/Badge';
import DeleteButton from '@components/ringil3/Buttons/DeleteButton';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import RingilSpinner from '@components/ringil3/RingilSpinner/RingilSpinner';
import { RowWithSpaceBetween } from '@components/styles.tsx';
import SvgExtracosts from '@components/ringil3/icons/heading/Extracosts';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { useCreateDeleteDialog } from '@components/ringil3/Dialog/DeleteDialog.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles';
import { formatPrice } from '@utils/priceUtils.ts';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { extraCostsTotal } from '@components/ringil3/Features/Expedition/ExtraCosts/extraCostsTotal.ts';

interface ComponentProps {
  companyId: string;
  applicationId: string;
  costs: ExtraCostsPreviewOutDTO[];
  onRemoved: () => void;
  contractualPrice?: number;
  agreedPrice?: number;
  agreedPriceCurrency?: string;
  extraCostSumPrice?: number;
  extraCostSumCurrency?: string;
  extraCostSumPriceInternal?: number;
  /** Suppress the own headline when the surrounding SectionPanel already provides one. */
  hideHeader?: boolean;
}

const ExtraCosts: React.FC<ComponentProps> = ({
  costs,
  applicationId,
  companyId,
  onRemoved,
  contractualPrice,
  agreedPrice,
  agreedPriceCurrency,
  extraCostSumPrice,
  extraCostSumCurrency,
  extraCostSumPriceInternal,
  hideHeader = false
}) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { cuDeleteExtraCostForExpedition } = useContext(ApiContext);
  const [apiRequest, setApiRequest] = useState(false);

  const { DeleteDialog, setObjToDelete } = useCreateDeleteDialog();
  const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();

  const deleteExtraCost = (extraCostId: string) => {
    setApiRequest(true);
    cuDeleteExtraCostForExpedition(
      companyId,
      applicationId,
      extraCostId,
      () => {
        setApiRequest(false);
        onRemoved();
      },
      null,
      {
        onValidationFailed: originalResponse => {
          setApiRequest(false);
          setBeValidationErrors(originalResponse);
        },
      },
    );
  };  

  const amountWithCurrency = useMemo(() => extraCostsTotal(contractualPrice, costs), [costs, contractualPrice]);

  const renderDate = (row: ExtraCostsPreviewOutDTO) => {
    return (
      <div className="flex-col">
        <div>{stringDateTimeWithLocaleFromApiStringInTz(row.createdAt, loggedUser.defaultTimezoneId)}</div>
        <COTimezone timezoneId={loggedUser.defaultTimezoneId} />
      </div>
    );
  };
  const renderCategory = (row: ExtraCostsPreviewOutDTO) => {
    return (
      <COBadge type={'neutral'} variant={'filled'}>
        {row.type}
      </COBadge>
    );
  };
  const renderDescription = (row: ExtraCostsPreviewOutDTO) => {
    return <div>{row.description}</div>;
  };
  const renderInternal = (row: ExtraCostsPreviewOutDTO) => {
    return (
      <div>
        {row.internal ? (
          <>
            <COBadge variant={'filled'} type={'warning'}>
              {tr(`ExtraCosts.yes`, ``)}
            </COBadge>
          </>
        ) : (
          <></>
        )}
      </div>
    );
  };
  const renderPrice = (row: ExtraCostsPreviewOutDTO) => {
    return (
      <div>
        <div>{formatPrice(row.priceDTO?.price, row.priceDTO?.currency)}</div>
      </div>
    );
  };
  const renderActions = (row: ExtraCostsPreviewOutDTO) => {
    return (
      <div>
        <DeleteButton onClick={() => setObjToDelete(row.id)} />
      </div>
    );
  };

  return (
    <>
      <div className="py-6">
        {/* headline hidden when a SectionPanel provides it; the spinner stays either way */}
        <RowWithSpaceBetween className={hideHeader ? 'justify-end' : undefined}>
          {!hideHeader && (
            <COHeadline variant={'h2'} title={tr(`ExtraCosts.heading`, `Total cost: {{amountWithCurrency}}`, { amountWithCurrency })} icon={<SvgExtracosts />}></COHeadline>
          )}
          <RingilSpinner isSpinning={apiRequest} />
        </RowWithSpaceBetween>
        <Ringil3BadgeRow className="mt-4 mb-4">
          {isNotNullOrUndefined(agreedPrice) && isNotNullOrUndefined(agreedPriceCurrency) && (
            <Badge 
              label={`${tr('ExtraCosts.agreedPrice', 'Domluvená cena')}`} 
              value={formatPrice(agreedPrice, agreedPriceCurrency)}
              borderColor='var(--ringil3-brand-light-color)'
            />
          )}
          {isNotNullOrUndefined(extraCostSumPrice) && isNotNullOrUndefined(extraCostSumCurrency) && (
            <Badge 
              label={`${tr('ExtraCosts.extraCostSumPrice', 'Celkové vícenáklady')}`} 
              value={
                isNotNullOrUndefined(extraCostSumPriceInternal)
                  ? `${formatPrice(extraCostSumPrice, extraCostSumCurrency)} (${tr('ExtraCosts.internalPart', 'z toho interní')}: ${formatPrice(extraCostSumPriceInternal, extraCostSumCurrency)})`
                  : formatPrice(extraCostSumPrice, extraCostSumCurrency)
              }
              borderColor='var(--ringil3-brand-light-color)'
            />
          )}
        </Ringil3BadgeRow>
        <BackendValidationComponent />
        <RingilDataTable
          columns={[
            { name: tr('ExtraCosts.on', 'On') },
            { name: tr('ExtraCosts.category', 'Category') },
            { name: tr('ExtraCosts.description', 'Description') },
            { name: tr('ExtraCosts.internal', 'Internal') },
            { name: tr('ExtraCosts.expense', 'Expense') },
            { name: '' },
          ]}
          renderers={[renderDate, renderCategory, renderDescription, renderInternal, renderPrice, renderActions]}
          data={costs}
        />
      </div>
      <DeleteDialog onDelete={objToDelete => deleteExtraCost(objToDelete)} />
    </>
  );
};

export default ExtraCosts;
